import { createSelector, createSlice } from '@reduxjs/toolkit';
import { merge, orderBy } from 'lodash';
import { matchPath } from 'react-router-dom';
import {
  ASPECT_RATIO,
  CONFIGURATOR_TYPE,
  DEFAULT_DOWNLOAD_POSITION,
  DEFAULT_FONT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_SORT_BY,
  DEFAULT_THEME_BACKGROUND_COLOR,
  EMPTY_CONFIGURATOR,
  OTHER_THEME_BACKGROUND_COLOR,
} from '../../../constants/productConfiguratorConstants';
import {
  PRODUCT_CONFIGURATOR_SCENES_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABELS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ADD_OPTION_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_PRODUCTS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_PRODUCT_ADD_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEWS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEW_DETAILS_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEW_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_SETTINGS_ROUTE,
  PRODUCT_CONFIGURATOR_TAB_ROUTE,
  PRODUCT_CONFIGURATOR_LABEL_OPTION_EDIT_ROUTE,
  PRODUCT_CONFIGURATOR_LABEL_OPTION_BASE_MATERIAL_ROUTE,
  PRODUCT_CONFIGURATOR_SCENE_VIEW_ADD_ROUTE,
} from '../../../constants/routePathConstants';
import {
  addItemToList,
  deleteListItem,
  sar,
  updateItemsInList,
  updateObject,
  updateOneItemInList,
} from '../../../helpers/reduxHelper';
import { COLOR_TYPE } from '../admin/curatorStylesSlicer';
import {
  selectSceneProducts,
  selectSceneProductsLoaded,
  selectSceneProductsLoading,
} from './configuratorSceneProducts.slicer';

export const TAB = {
  CONFIG: 'config',
  THEME: 'theme',
  SETTINGS: 'settings',
};

const LS_CONFIGURATOR_SIDEBAR_OPEN = 'LF_CONFIGURATOR_SIDEBAR_OPEN';

const getDefaultSidebarOopenValue = () => {
  const lsValue = localStorage.getItem(LS_CONFIGURATOR_SIDEBAR_OPEN);
  return lsValue === 'false' ? false : true;
};

const getInitialState = () => ({
  sidebarOpen: getDefaultSidebarOopenValue(),
  activeTab: TAB.CONFIG, // getDefaultTabValue(), // TAB.SETTINGS,
  // selectedScene: '',
  unityLoaded: false,
  lightingPreview: {
    id: null,
  },
  addLabelActive: false,
  sidebarFooterRendered: false,
  nameEditing: false,
  lightingNameEditing: false,
  sceneId: null, // we want to keep same scene selected even if url changed
  colorType: COLOR_TYPE.RGB,
  allSceneInitPropsLoaded: false,
  unitySceneLoaded: false,
  objectsForNewLabelOption: null, // when user right clicks on an object and wants to create new label option for that object
  canInitUnityScene: false,
  unityBaseSceneReady: false,
  isCreateConfiguratorFlow: false, // when we create configurator we immediately redirect user to created configurator -> we need this variable to prevent data fetching when user is redirected
  isUnityInitializing: false,
  dataLoading: false,
  dataLoaded: false,
  data: EMPTY_CONFIGURATOR,
  scenesLoading: false,
  scenesLoaded: false,
  scenesError: false,
  scenes: [],
  initialObjectProperties: [],
  materialToUpdate: [],
  // selectedOption: {},
  calledOnce: false,
  allThemes: [],
  selectedTheme: {},
  themeLoading: false,
  allAspectRatios: [],
  unityHovered: false,
  baseMaterialData: null,
  renderPreviewData: [],
  initialMaterialApplied: false,
  showThemePopup: false,
  objectsWithDefaultThumbnail: [],
  allDataProcessed: false,
  deleteLoading: false,
  backupLabels: [],
  defaultTextures: null,
  addRenderersCount: 0,
  thumbnailUpdatedOptions: [],
  backupDefaultTextures: [],
  editTextureActive: false,
  watermarkInfo: {
    scale: 1,
    opacity: 1,
    x: 0,
    y: 0,
  },
  defaultMaterialData: null,
  addLabelSuccess: null,
  currentLoadedTemplate: null,
  allContentType: null,
  showFullScreenLoader: false,
  isViewThreeSixty: false,
  unityLightroomDataWithProps: null,
  selectedLightroomUnityWindow: null,
  selectedViewInPrototype: null,
  lightroomCalculatedRoomId: null,
  lightroomCalculatedBaseRoomId: null,
  lightroomToggleActive: false,
  livePreviewActive: true,
  isV1LightroomLoaded: false,
  prototypeLoading: false,
  unlinkedObjectsAfterDelete: [],
  allOptionsLoaded: false,
  isDataChanged: false,
  objectsAfterDeleteOperationCompleted: {
    rawData: [],
    parentData: [],
  },
  textureLinkingPopup: null,
  textureLinkingData: [],
  textureLinkingRawData: null,
  prototypeTextureLinkingData: {},
});

const configurator = createSlice({
  name: 'configurator',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    setSidebarOpen: (state, { payload }) => {
      state.sidebarOpen = payload;
      localStorage.setItem(LS_CONFIGURATOR_SIDEBAR_OPEN, payload);
    },
    setColorType: sar('colorType'),
    setActiveTab: sar('activeTab'),
    setUnityLoaded: sar('unityLoaded'),
    setSceneId: sar('sceneId'),
    setSidebarFooterRendered: sar('sidebarFooterRendered'),
    setNameEditing: sar('nameEditing'),
    setLightingNameEditing: sar('lightingNameEditing'),
    setSelectedScene: sar('selectedScene'),
    setAddLabelActive: sar('addLabelActive'),
    setCanInitUnityScene: sar('canInitUnityScene'),
    setUnityBaseSceneReady: sar('unityBaseSceneReady'),
    setIsCreateConfiguratorFlow: sar('isCreateConfiguratorFlow'),
    setIsUnityInitializing: sar('isUnityInitializing'),
    setAllSceneInitPropsLoaded: sar('allSceneInitPropsLoaded'),
    setUnitySceneLoaded: sar('unitySceneLoaded'),
    setRenderPreviewData: sar('renderPreviewData'),
    setTheme: (state, { payload }) => {
      state.data.theme = payload;
    },
    updateConfigurator: updateObject('data'),
    fetchScenesStart: (state) => {
      state.scenes = [];
      state.scenesLoading = true;
      state.scenesLoaded = false;
      state.scenesError = false;
    },
    fetchScenesSuccess: (state, { payload }) => {
      state.scenes = payload;
      state.scenesLoading = false;
      state.scenesLoaded = true;
      state.scenesError = false;
    },
    fetchScenesFailed: (state) => {
      state.scenes = [];
      state.scenesLoading = false;
      state.scenesLoaded = false;
      state.scenesError = true;
    },
    setSceneNextPage: (state, { payload }) => {
      const { configuratorId, nextPage } = payload;
      state[configuratorId] = { sceneNextPage: nextPage };
    },
    setSceneCurrentPage: (state, { payload }) => {
      const { configuratorId, currentPage } = payload;
      state[configuratorId] = { sceneCurrentPage: currentPage };
    },
    setSceneLoading: (state, { payload }) => {
      const { configuratorId, value } = payload;
      if (state[configuratorId]) {
        state[configuratorId].loading = value;
      } else state[configuratorId] = { loading: value };
    },
    addScene: addItemToList('scenes'),
    deleteScene: deleteListItem('scenes'),
    setObjectsForNewLabelOption: (state, { payload }) => {
      const { objectNames } = payload;
      state.objectsForNewLabelOption = objectNames;
    },
    fetchDataStart: (state) => {
      state.dataLoading = true;
      state.data = EMPTY_CONFIGURATOR;
    },
    fetchDataSuccess: (state, { payload }) => {
      state.dataLoading = false;
      state.dataLoaded = true;
      state.data = payload;
    },
    fetchDataFailed: (state, { payload }) => {
      state.dataLoading = false;
      state.dataLoaded = true;
      state.data = {};
    },
    setSceneName: (state, { payload }) => {
      const { sceneId, name } = payload;
      state.scenes = state.scenes.map((scene) => {
        return scene.id === sceneId ? { ...scene, name } : scene;
      });
    },
    updateScene: updateOneItemInList('scenes'),
    updateScenes: updateItemsInList('scenes', 'scenes'),
    updateData: (state, { payload }) => {
      state.data = merge({}, state.data, payload);
    },
    openLightingPreview: (state, { payload }) => {
      const { lightingId } = payload;
      state.lightingPreview.id = lightingId;
    },
    closeLightingPreview: (state) => {
      state.lightingPreview.id = null;
    },
    setInitialObjectProperties: (state, { payload }) => {
      state.initialObjectProperties.push(payload);
    },
    updateInitialObjectProperties: (state, { payload }) => {
      state.initialObjectProperties = payload;
    },
    setMaterialToUpdate: (state, { payload }) => {
      state.materialToUpdate = payload;
    },
    // setSelectedOption: (state, { payload }) => {
    //   if (payload?.sceneId && !state?.selectedOption?.[payload?.sceneId]) {
    //     state.selectedOption[payload.sceneId] = {};
    //   }
    //   if (payload?.sceneId && payload?.labelId) {
    //     state.selectedOption[payload.sceneId][payload.labelId] = payload.data;
    //   }
    // },
    // setSelectedOptionAll: (state, { payload }) => {
    //   state.selectedOption = payload;
    // },
    setCalledOnce: (state, { payload }) => {
      state.calledOnce = payload;
    },
    setAllThemes: (state, { payload }) => {
      state.allThemes = payload;
    },
    setAllAspectRatio: (state, { payload }) => {
      state.allAspectRatios = payload;
    },
    setSelectedTheme: (state, { payload }) => {
      state.selectedTheme = payload;
    },
    setThemeLoading: (state, { payload }) => {
      state.themeLoading = payload;
    },
    setUnityHovered: (state, { payload }) => {
      state.unityHovered = payload;
    },
    setBaseMaterialData: (state, { payload }) => {
      state.baseMaterialData = payload;
    },
    setInitialMaterialApplied: (state, { payload }) => {
      state.initialMaterialApplied = payload;
    },
    setThemePopup: (state, { payload }) => {
      state.showThemePopup = payload;
    },
    setObjectsWithDefaultThumbnail: (state, { payload }) => {
      state.objectsWithDefaultThumbnail.push(payload);
    },
    setAllDataProcessed: (state, { payload }) => {
      state.allDataProcessed = payload;
    },
    setDeleteLoading: (state, { payload }) => {
      state.deleteLoading = payload;
    },
    setBackupLabels: (state, { payload }) => {
      state.backupLabels = payload;
    },
    setDefaultTextures: (state, { payload }) => {
      state.defaultTextures = payload;
    },
    setBackupDefaultTextures: (state, { payload }) => {
      state.backupDefaultTextures = payload;
    },
    setAddRenderersCount: (state, { payload }) => {
      state.addRenderersCount = payload;
    },
    setThumbnailUpdatedOptions: (state, { payload }) => {
      state.thumbnailUpdatedOptions = payload;
    },
    setCurrentConfigurator: (state, { payload }) => {
      state.currentConfigurator = payload;
    },
    setIsWatermarkEnabled: (state, { payload }) => {
      state.isWatermarkEnabled = payload;
    },
    setWatermarkInfo: (state, { payload }) => {
      state.watermarkInfo = payload;
    },
    setEditTextureActive: (state, { payload }) => {
      state.editTextureActive = payload;
    },
    setCurrentLoadedTemplate: (state, { payload }) => {
      state.currentLoadedTemplate = payload;
    },
    setAllContentType: (state, { payload }) => {
      state.allContentType = payload;
    },
    setShowFullScreenLoader: (state, { payload }) => {
      state.showFullScreenLoader = payload;
    },
    setDefaultMaterialData: (state, { payload }) => {
      state.defaultMaterialData = payload;
    },
    setAddLabelSuccess: (state, { payload }) => {
      state.addLabelSuccess = payload;
    },
    setIsViewThreeSixty: (state, { payload }) => {
      state.isViewThreeSixty = payload;
    },
    setUnityLightroomDataWithProps: (state, { payload }) => {
      state.unityLightroomDataWithProps = payload;
    },
    setSelectedLightroomUnityWindow: (state, { payload }) => {
      state.selectedLightroomUnityWindow = payload;
    },
    setSelectedViewInPrototype: (state, { payload }) => {
      state.selectedViewInPrototype = payload;
    },
    setLightroomCalculatedRoomId: (state, { payload }) => {
      state.lightroomCalculatedRoomId = payload;
    },
    setLightroomCalculatedBaseRoomId: (state, { payload }) => {
      state.lightroomCalculatedBaseRoomId = payload;
    },
    setLightroomToggleActive: (state, { payload }) => {
      state.lightroomToggleActive = payload;
    },
    setLivePreviewActive: (state, { payload }) => {
      state.livePreviewActive = payload;
    },
    setIsV1LightroomLoaded: (state, { payload }) => {
      state.isV1LightroomLoaded = payload;
    },
    setPrototypeLoading: (state, { payload }) => {
      state.prototypeLoading = payload;
    },
    setUnlinkedObjectsAfterDelete: (state, { payload }) => {
      state.unlinkedObjectsAfterDelete = [...state.unlinkedObjectsAfterDelete, ...payload];
    },
    resetUnlinkedObjectsAfterDelete: (state, { payload }) => {
      state.unlinkedObjectsAfterDelete = payload;
    },
    setAllOptionsLoaded: (state, { payload }) => {
      state.allOptionsLoaded = payload;
    },
    setObjectsAfterDeleteOperationCompleted: (state, { payload }) => {
      state.objectsAfterDeleteOperationCompleted = payload;
    },
    setIsDataChanged: sar('isDataChanged'),
    setTextureLinkingPopup: (state, { payload }) => {
      state.textureLinkingPopup = payload;
    },
    setTextureLinkingData: (state, { payload }) => {
      state.textureLinkingData = payload;
    },
    setTextureLinkingRawData: (state, { payload }) => {
      state.textureLinkingRawData = payload;
    },
    setPrototypeTextureLinkingData: (state, { payload }) => {
      const { sceneId, data } = payload;
      state.prototypeTextureLinkingData[sceneId] = data;
    },
  },
});

export const configuratorActions = configurator.actions;
export const configuratorReducer = configurator.reducer;
export default configuratorReducer;

export const selectPrototypeTextureLinkingData = (state) =>
  state.configurator.prototypeTextureLinkingData;
export const selectTextureLinkingRawData = (state) => state.configurator.textureLinkingRawData;
export const selectTextureLinkingData = (state) => state.configurator.textureLinkingData;
export const selectTextureLinkingPopup = (state) => state.configurator.textureLinkingPopup;
export const selectObjectsAfterDeleteOperationCompleted = (state) =>
  state.configurator.objectsAfterDeleteOperationCompleted;
export const selectIsDataChanged = (state) => state.configurator.isDataChanged;
export const selectAllOptionsLoaded = (state) => state.configurator.allOptionsLoaded;
export const selectUnlinkedObjectsAfterDelete = (state) =>
  state.configurator.unlinkedObjectsAfterDelete;
export const selectPrototypeLoading = (state) => state.configurator.prototypeLoading;
export const selectLivePreviewActive = (state) => state.configurator.livePreviewActive;
export const selectSelectedViewInPrototype = (state) => state.configurator.selectedViewInPrototype;
export const selectIsViewThreeSixty = (state) => state.configurator.isViewThreeSixty;
export const selectAddLabelSuccess = (state) => state.configurator.addLabelSuccess;
export const selectDefaultMaterialData = (state) => state.configurator.defaultMaterialData;
export const selectPrototypeScenesLoading = (configuratorId) => (state) =>
  state?.[configuratorId]?.loading;
export const selectEditTextureActive = (state) => state.configurator.editTextureActive;
export const selectCurrentConfigurator = (state) => state.configurator.currentConfigurator;
export const selectThumbnailUpdatedOptions = (state) => state.configurator.thumbnailUpdatedOptions;
export const selectAddRenderersCount = (state) => state.configurator.addRenderersCount;
export const selectBackupDefaultTextures = (state) => state.configurator.backupDefaultTextures;
export const selectDefaultTextures = (state) => state.configurator.defaultTextures;
export const selectBackupLabels = (state) => state.configurator.backupLabels;
export const selectDeleteLoading = (state) => state.configurator.deleteLoading;
export const selectAllDataProcessed = (state) => state.configurator.allDataProcessed;
export const selectObjectsWithDefaultThumbnail = (state) =>
  state.configurator.objectsWithDefaultThumbnail;
export const selectThemePopup = (state) => state.configurator.showThemePopup;
export const selectInitialMaterialApplied = (state) => state.configurator.initialMaterialApplied;
export const selectBaseMaterialData = (state) => state.configurator.baseMaterialData;
export const selectUnityHovered = (state) => state.configurator.unityHovered;
export const selectThemeLoading = (state) => state.configurator.themeLoading;
export const selectSelectedTheme = (state) => state.configurator.selectedTheme;
export const selectAllThemes = (state) => state.configurator.allThemes;
export const selectCalledOnce = (state) => state.configurator.calledOnce;
// export const selectSelectedOption = (state) => state.configurator.selectedOption;
export const selectMaterialToUpdate = (state) => state.configurator.materialToUpdate;
export const selectInitialObjectProperties = (state) => state.configurator.initialObjectProperties;
export const selectSidebarOpen = (state) => state.configurator.sidebarOpen;
export const selectDataLoading = (state) => state.configurator.dataLoading;
export const selectDataLoaded = (state) => state.configurator.dataLoaded;
export const selectCanInitUnityScene = (state) => state.configurator.canInitUnityScene;
export const selectUnityBaseSceneReady = (state) => state.configurator.unityBaseSceneReady;
export const selectLightingPreviewId = (state) => state.configurator.lightingPreview.id;
export const selectSidebarFooterRendered = (state) => state.configurator.sidebarFooterRendered;
export const selectIsCreateConfiguratorFlow = (state) =>
  state.configurator.isCreateConfiguratorFlow;
export const selectIsUnityInitializing = (state) => state.configurator.isUnityInitializing;
export const selectNameEditing = (state) => state.configurator.nameEditing;
export const selectLightingNameEditing = (state) => state.configurator.lightingNameEditing;
export const selectObjectsForNewLabelOption = (state) =>
  state.configurator.objectsForNewLabelOption;
export const selectColorType = (state) => state.configurator.colorType;
export const selectAllSceneInitPropsLoaded = (state) => state.configurator.allSceneInitPropsLoaded;
export const selectUnitySceneLoaded = (state) => state.configurator.unitySceneLoaded;
export const selectConfiguratorData = (state) => state.configurator.data;
export const selectConfiguratorIsWatermark = (state) => state.configurator.data.is_watermark;
export const selectConfiguratorWatermarkImage = (state) => state.configurator.data.watermark;
export const selectConfiguratorThumbImage = (state) => state.configurator.data.thumb;
export const selectConfiguratorVersion = (state) => state.configurator.data.version_type;
export const selectConfiguratorSharingEnabled = (state) =>
  state.configurator.data.is_enable_sharing;
export const selectConfiguratorDownloadEnabled = (state) =>
  state.configurator.data.is_download_enabled;
export const selectConfiguratorIsAutoRotateEnabled = (state) =>
  state.configurator.data.is_auto_rotate_disabled;
export const selectConfiguratorIsScenePanelEnabled = (state) =>
  state.configurator.data.is_scene_panel_enabled;
export const selectConfiguratorIsShadow = (state) => state.configurator.data.is_shadow;
export const selectConfiguratorType = (state) => state.configurator.data.config_type;
export const selectIsImageConfigurator = (state) =>
  selectConfiguratorType(state) === CONFIGURATOR_TYPE.IMAGE;
export const selectIs360Configurator = (state) =>
  selectConfiguratorType(state) === CONFIGURATOR_TYPE.THREE_SIXTY;
export const selectActiveTheme = (state) => state.configurator.data.theme;
export const selectConfiguratorName = (state) => state.configurator.data.name;
export const selectConfiguratorAspectRatio = (state) => {
  const ratio = state.configurator.data.aspect_ratio;
  return ASPECT_RATIO[ratio] || ASPECT_RATIO['1.33'];
};
export const selectConfiguratorFontSize = (state) => {
  return state.configurator.data?.font_settings?.size || DEFAULT_FONT_SIZE;
};
export const selectConfiguratorFontOverflow = (state) => {
  return state.configurator.data?.font_settings?.allow_overflow;
};
export const selectConfiguratorSortBy = (state) => {
  return state.configurator.data?.sort_by || DEFAULT_SORT_BY;
};
export const selectConfiguratorDownloadIconPosition = (state) => {
  return state.configurator.data?.download_icon_location || DEFAULT_DOWNLOAD_POSITION;
};
export const selectConfiguratorDownloadIconThumbnail = (state) => {
  return state.configurator.data?.download_icon;
};
export const selectConfiguratorThemeBackgroundColor = (state) => {
  const defaultColor = state.configurator.selectedTheme === 12 ? DEFAULT_THEME_BACKGROUND_COLOR : OTHER_THEME_BACKGROUND_COLOR;
  return state.configurator.data?.theme_settings?.background_color || defaultColor;
};
export const selectConfiguratorThemeFontColor = (state) => {
  return state.configurator.data?.theme_settings?.font_color || DEFAULT_FONT_COLOR;
};
export const selectScenes = (state) => state.configurator.scenes;
export const selectScenesLoading = (state) => state.configurator.scenesLoading;
export const selectScenesLoaded = (state) => state.configurator.scenesLoaded;
export const selectFailedToLoadScenes = (state) => Boolean(state.configurator.scenesError);
export const selectAddLabelActive = (state) => state.configurator.addLabelActive;
export const selectRenderPreview = (state) => state.configurator.renderPreviewData;
const createPathnameSelector = (...args) => createSelector(() => window.location.pathname, ...args);
export const selectCurrentLoadedTemplate = (state) => state.configurator.currentLoadedTemplate;

export const selectSidebarTab = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_TAB_ROUTE + '/*' }, pathname);
  return match?.params?.tabName || TAB.CONFIG;
});

export const selectSelectedSceneId = createPathnameSelector(
  (state) => state.configurator.sceneId,
  (pathname, sceneId) => {
    const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_ROUTE + '/*' }, pathname);
    return parseInt(match?.params?.sceneId) || sceneId;
  }
);

export const selectSortedScenes = createSelector(
  (state) => selectScenes(state),
  (scenes) => orderBy(scenes, 'seq_no', 'asc')
);

export const selectSceneById = (sceneId) => (state) => {
  return state.configurator.scenes.find((scene) => scene.id === sceneId);
};

export const selectSelectedScene = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneById(sceneId)(state);
};

export const selectCurrentScene = selectSelectedScene;

export const selectCurrentSceneName = (state) => selectCurrentScene(state)?.name;

export const selectConfiguratorId = (state) => state.configurator.data?.id;

export const selectIsSceneRoute = createPathnameSelector((pathname) => {
  const matchView = matchPath({ path: PRODUCT_CONFIGURATOR_SCENES_ROUTE + '/*' }, pathname);
  return Boolean(matchView);
});

export const selectSelectedViewId = createPathnameSelector((pathname) => {
  const matchView = matchPath(
    { path: PRODUCT_CONFIGURATOR_SCENE_VIEW_DETAILS_ROUTE + '/*' },
    pathname
  );
  const matchViewSettings = matchPath(
    { path: PRODUCT_CONFIGURATOR_SCENE_VIEW_SETTINGS_ROUTE + '/*' },
    pathname
  );
  return parseInt(matchView?.params?.viewId) || parseInt(matchViewSettings?.params?.viewId);
});

export const selectSelectedLabelId = createPathnameSelector((pathname) => {
  const match = matchPath(
    { path: PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ROUTE + '/*' },
    pathname
  );
  return parseInt(match?.params?.labelId);
});

export const selectSelectedOptionId = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_LABEL_OPTION_EDIT_ROUTE + '/*' }, pathname);
  return parseInt(match?.params?.optionId);
});

export const selectSelectedOptionIdFromBaseMaterial = createPathnameSelector((pathname) => {
  const match = matchPath(
    { path: PRODUCT_CONFIGURATOR_LABEL_OPTION_BASE_MATERIAL_ROUTE },
    pathname
  );
  return match?.params?.optionId;
});

export const selectUnityLoaded = (state) => state.configurator.unityLoaded;

export const selectIsProductsRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_PRODUCTS_ROUTE + '/*' }, pathname);
  return Boolean(match);
});

export const selectIsViewsRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_VIEWS_ROUTE + '/*' }, pathname);
  return Boolean(match);
});

export const selectIsViewSettingsRoute = createPathnameSelector((pathname) => {
  const match = matchPath(
    { path: PRODUCT_CONFIGURATOR_SCENE_VIEW_SETTINGS_ROUTE + '/*' },
    pathname
  );
  return Boolean(match);
});

export const selectIsAddProductRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_PRODUCT_ADD_ROUTE }, pathname);
  return Boolean(match);
});

export const selectIsAddViewRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_VIEW_ADD_ROUTE }, pathname);
  return Boolean(match);
});

export const selectIsLabelsRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_SCENE_LABELS_ROUTE + '/*' }, pathname);
  return Boolean(match);
});

export const selectIsLabelOptionEditRoute = createPathnameSelector((pathname) => {
  const match = matchPath({ path: PRODUCT_CONFIGURATOR_LABEL_OPTION_EDIT_ROUTE + '/*' }, pathname);
  return Boolean(match);
});

export const selectSelectedSceneProducts = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneProducts(sceneId)(state);
};

export const selectSelectedSceneProductsLoading = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneProductsLoading(sceneId)(state);
};

export const selectSelectedSceneProductsLoaded = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneProductsLoaded(sceneId)(state);
};

export const selectIsAddOption = createPathnameSelector((pathname) => {
  const match = matchPath(
    { path: PRODUCT_CONFIGURATOR_SCENE_LABEL_DETAILS_ADD_OPTION_ROUTE },
    pathname
  );
  return Boolean(match);
});

export const selectSettingsRoute = (state, exact = false) => {
  const suffix = exact ? '' : '/*';
  const match = matchPath(
    { path: PRODUCT_CONFIGURATOR_SETTINGS_ROUTE + suffix },
    window.location.pathname
  );
  return match ? false : window.location.pathname.split('/').at(-1);
};

export const selectAllAspectRatio = (state) => {
  return state.configurator.allAspectRatios;
};

export const selectIsWatermarkEnabled = (state) => {
  return state.configurator.isWatermarkEnabled;
};

export const selectWatermarkInfo = (state) => {
  return state.configurator.watermarkInfo;
};

export const selectAllContentType = (state) => {
  return state.configurator.allContentType;
};

export const selectShowFullScreenLoader = (state) => {
  return state.configurator.showFullScreenLoader;
};

export const selectUnityLightroomDataWithProps = (state) => {
  return state.configurator.unityLightroomDataWithProps;
};

export const selectSelectedLightroomUnityWindow = (state) => {
  return state.configurator.selectedLightroomUnityWindow;
};

export const selectLightroomCalculatedId = (state) => {
  return state.configurator.lightroomCalculatedRoomId;
};

export const selectLightroomCalculatedBaseRoomId = (state) => {
  return state.configurator.lightroomCalculatedBaseRoomId;
};

export const selectLightroomToggleActive = (state) => {
  return state.configurator.lightroomToggleActive;
};

export const getNextScenePage = (configuratorId) => (state) => {
  return state?.configurator?.[configuratorId]?.sceneNextPage;
};

export const selectIsV1LightroomLoaded = (state) => {
  return state.configurator.isV1LightroomLoaded;
};
